import React from 'react'
import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import PetsIcon from '@material-ui/icons/Pets'
import PeopleIcon from '@material-ui/icons/People'
import PersonAddIcon from '@material-ui/icons/PersonAdd'
import BlockIcon from '@material-ui/icons/Block'
import ReplayIcon from '@material-ui/icons/Replay'
import SwapHorizIcon from '@material-ui/icons/SwapHoriz'
import PersonOutlineIcon from '@material-ui/icons/PersonOutline'
import StarIcon from '@material-ui/icons/Star'
import RemoveIcon from '@material-ui/icons/Remove'
import { NextActionIcon } from '../components/NextActionMark'

const useStyles = makeStyles((theme: Theme) => ({
    header: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        paddingRight: theme.spacing(1),
    },
    section: {
        marginBottom: theme.spacing(2.5),
        '&:last-child': {
            marginBottom: 0,
        },
    },
    sectionTitle: {
        fontSize: '0.75rem',
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1),
    },
    row: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(1.25),
    },
    icon: {
        width: 28,
        display: 'flex',
        justifyContent: 'center',
        paddingTop: 2,
        flexShrink: 0,
        fontSize: 20,
    },
    title: {
        fontWeight: 700,
        fontSize: '0.875rem',
    },
    body: {
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
        marginTop: 2,
    },
}))

function LegendRow({
    icon,
    title,
    children,
}: {
    icon: React.ReactNode
    title: string
    children: React.ReactNode
}) {
    const classes = useStyles()
    return (
        <Box className={classes.row}>
            <Box className={classes.icon}>{icon}</Box>
            <Box>
                <Typography className={classes.title}>{title}</Typography>
                <Typography className={classes.body}>{children}</Typography>
            </Box>
        </Box>
    )
}

export function OccupancyIconLegendDialog({
    open,
    onClose,
}: {
    open: boolean
    onClose: () => void
}) {
    const classes = useStyles()
    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <Box className={classes.header}>
                <DialogTitle disableTypography>
                    <Typography variant="h6">Occupancy icons</Typography>
                    <Typography variant="body2" color="textSecondary">
                        Hover an icon on a person for names and details. This
                        list is the full key.
                    </Typography>
                </DialogTitle>
                <IconButton onClick={onClose} aria-label="Close">
                    <CloseIcon />
                </IconButton>
            </Box>
            <DialogContent dividers>
                <Box className={classes.section}>
                    <Typography className={classes.sectionTitle}>
                        Next action
                    </Typography>
                    <LegendRow
                        icon={<NextActionIcon owner="us" fontSize={20} />}
                        title="Needs you"
                    >
                        Amber mark. Staff has the next move — assign a room,
                        confirm a suggestion, or another action on this person.
                    </LegendRow>
                    <LegendRow
                        icon={<NextActionIcon owner="them" fontSize={20} />}
                        title="Waiting on them"
                    >
                        Slate hourglass. They still owe a step, such as the
                        roommate profile. Hover the icon for the action.
                    </LegendRow>
                </Box>
                <Box className={classes.section}>
                    <Typography className={classes.sectionTitle}>
                        Lease type
                    </Typography>
                    <LegendRow
                        icon={
                            <ReplayIcon
                                style={{ fontSize: 20, color: '#047857' }}
                            />
                        }
                        title="Renewal"
                    >
                        Staying in a bed for next year. Confirmed renewals
                        cannot be moved or unassigned.
                    </LegendRow>
                    <LegendRow
                        icon={
                            <SwapHorizIcon
                                style={{ fontSize: 20, color: '#b45309' }}
                            />
                        }
                        title="Transfer"
                    >
                        Asked to change units. Treat like a new placement until
                        a transfer move is built.
                    </LegendRow>
                    <LegendRow
                        icon={
                            <PersonOutlineIcon
                                style={{ fontSize: 20, color: '#3730a3' }}
                            />
                        }
                        title="New move-in"
                    >
                        Incoming resident. Can be suggested, confirmed, moved,
                        or removed from a unit.
                    </LegendRow>
                </Box>

                <Box className={classes.section}>
                    <Typography className={classes.sectionTitle}>
                        Roommate requests
                    </Typography>
                    <LegendRow
                        icon={
                            <PeopleIcon
                                style={{ fontSize: 20, color: '#6b7280' }}
                            />
                        }
                        title="Requested roommate (matched)"
                    >
                        They named someone in occupancy, and we can treat it as
                        mutual — typically the other person named them back.
                        This is not “already placed in the same unit.” Hover for
                        names.
                    </LegendRow>
                    <LegendRow
                        icon={
                            <PersonAddIcon
                                style={{ fontSize: 20, color: '#6b7280' }}
                            />
                        }
                        title="Requested roommate (not matched)"
                    >
                        They named someone we could not treat as mutual: missing
                        from occupancy, email did not resolve, or the other
                        person did not name them back. Hover for the request.
                        Both icons can appear if the list has more than one
                        name.
                    </LegendRow>
                </Box>

                <Box className={classes.section}>
                    <Typography className={classes.sectionTitle}>
                        Pets
                    </Typography>
                    <LegendRow
                        icon={
                            <PetsIcon
                                style={{ fontSize: 20, color: '#6b7280' }}
                            />
                        }
                        title="Has a pet"
                    >
                        They have a pet on file. Hover when details are
                        available.
                    </LegendRow>
                    <LegendRow
                        icon={
                            <BlockIcon
                                style={{ fontSize: 20, color: '#6b7280' }}
                            />
                        }
                        title="Not okay with roommate pets"
                    >
                        They said they are not okay living with a roommate who
                        has a pet. This is not an unmatched roommate request.
                    </LegendRow>
                </Box>

                <Box className={classes.section}>
                    <Typography className={classes.sectionTitle}>
                        Lifestyle weighting
                    </Typography>
                    <LegendRow
                        icon={
                            <StarIcon
                                style={{ fontSize: 20, color: '#c2410c' }}
                            />
                        }
                        title="Must"
                    >
                        This dimension is a hard preference. A conflict with
                        someone who also marked Must can flag the unit.
                    </LegendRow>
                    <LegendRow
                        icon={
                            <RemoveIcon
                                style={{ fontSize: 20, color: '#6b7280' }}
                            />
                        }
                        title="No preference"
                    >
                        They answered the question but said it does not matter.
                        The value is shown muted. Prefer (the default) has no
                        icon — just the lifestyle value.
                    </LegendRow>
                </Box>
            </DialogContent>
        </Dialog>
    )
}
